import React, { useContext, useMemo } from "react";
import { Doughnut, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { FaEdit, FaTrash } from "react-icons/fa";
import { GlobalContext } from "../../Utils/GlobalContext";

ChartJS.register(ArcElement, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const COR_SITUACAO = {
  A: "#28a745", V: "#dc3545", I: "#6c757d", P: "#ffc107", E: "#fd7e14",
  "Sim": "#dc3545", "Nao": "#28a745", "Sem vencimento": "#6c757d",
};
const PALETA = [
  "#0d6efd", "#28a745", "#dc3545", "#ffc107", "#6f42c1",
  "#0dcaf0", "#fd7e14", "#20c997", "#e83e8c", "#6610f2",
];

function corParaLabel(label, idx) {
  return COR_SITUACAO[label] ?? PALETA[idx % PALETA.length];
}

function agregarDados(dados, campo_grupo, campo_split) {
  if (!dados?.length) return null;

  if (!campo_grupo) {
    return { tipo: "kpi", total: dados.length };
  }

  if (!campo_split) {
    const contagem = {};
    dados.forEach((row) => {
      const val = row[campo_grupo] ?? "—";
      contagem[val] = (contagem[val] || 0) + 1;
    });
    const sorted = Object.entries(contagem).sort((a, b) => b[1] - a[1]);
    return {
      tipo: "single",
      dados: sorted.map(([label, total]) => ({ label, total })),
      total: dados.length,
    };
  }

  const matrix = {};
  const gruposSet = new Set();
  const splitsSet = new Set();
  dados.forEach((row) => {
    const g = row[campo_grupo] ?? "—";
    const s = row[campo_split] ?? "—";
    gruposSet.add(g);
    splitsSet.add(s);
    if (!matrix[g]) matrix[g] = {};
    matrix[g][s] = (matrix[g][s] || 0) + 1;
  });
  return { tipo: "split", grupos: [...gruposSet], splits: [...splitsSet], matrix };
}

function buildChartData(resposta) {
  if (!resposta) return null;

  if (resposta.tipo === "single") {
    return {
      labels: resposta.dados.map((d) => d.label),
      datasets: [{
        data: resposta.dados.map((d) => d.total),
        backgroundColor: resposta.dados.map((d, i) => corParaLabel(d.label, i)),
        borderWidth: 1,
        borderColor: "transparent",
      }],
    };
  }

  if (resposta.tipo === "split") {
    const { grupos, splits, matrix } = resposta;
    return {
      labels: grupos,
      datasets: splits.map((sp, idx) => ({
        label: sp,
        data: grupos.map((g) => matrix[g]?.[sp] ?? 0),
        backgroundColor: corParaLabel(sp, idx),
        borderRadius: 3,
      })),
    };
  }

  return null;
}

const OPCOES_BASE = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { position: "bottom", labels: { font: { size: 11 }, padding: 8, boxWidth: 12 } },
    tooltip: { callbacks: {} },
  },
};

const OPCOES_DONUT = {
  ...OPCOES_BASE,
  cutout: "60%",
  plugins: {
    ...OPCOES_BASE.plugins,
    tooltip: {
      callbacks: {
        label: (ctx) => {
          const total = ctx.dataset.data.reduce((a, b) => a + b, 0);
          const pct = total > 0 ? ((ctx.parsed / total) * 100).toFixed(1) : 0;
          return ` ${ctx.label}: ${ctx.parsed} (${pct}%)`;
        },
      },
    },
  },
};

const OPCOES_BAR_V = {
  ...OPCOES_BASE,
  indexAxis: "x",
  scales: {
    y: { beginAtZero: true, ticks: { stepSize: 1, font: { size: 11 } } },
    x: { ticks: { font: { size: 11 } } },
  },
};

const OPCOES_BAR_H = {
  ...OPCOES_BASE,
  indexAxis: "y",
  scales: {
    x: { beginAtZero: true, ticks: { font: { size: 11 } } },
    y: { ticks: { font: { size: 11 } } },
  },
  plugins: {
    ...OPCOES_BASE.plugins,
    tooltip: { callbacks: { label: (ctx) => ` ${ctx.dataset.label ?? ""}: ${ctx.parsed.x}` } },
  },
};

const OPCOES_BAR_H_PCT = {
  ...OPCOES_BAR_H,
  scales: {
    x: { beginAtZero: true, max: 100, ticks: { callback: (v) => `${v}%`, font: { size: 11 } } },
    y: { ticks: { font: { size: 11 } } },
  },
};

function StackedBarRows({ grupos, splits, matrix, texto, onDrillDown }) {
  return (
    <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: 7 }}>
      {grupos.map((grupo) => {
        const total = splits.reduce((s, sp) => s + (matrix[grupo]?.[sp] ?? 0), 0);
        if (!total) return null;
        return (
          <div key={grupo} style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div
              style={{ width: 84, flexShrink: 0, fontSize: "0.7rem", fontWeight: 700, color: texto, textAlign: "right", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", cursor: onDrillDown ? "pointer" : "default" }}
              title={grupo}
              onClick={() => onDrillDown?.(grupo, null, false)}
            >
              {grupo}
            </div>
            <div style={{ flex: 1, display: "flex", height: 24, borderRadius: 4, overflow: "hidden" }}>
              {splits.map((sp, si) => {
                const val = matrix[grupo]?.[sp] ?? 0;
                const pct = (val / total) * 100;
                if (pct < 0.01) return null;
                return (
                  <div
                    key={sp}
                    style={{ width: `${pct}%`, background: corParaLabel(sp, si), display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", transition: "width 0.25s", cursor: onDrillDown ? "pointer" : "default" }}
                    title={`${sp}: ${val} (${pct.toFixed(1)}%)`}
                    onClick={() => onDrillDown?.(grupo, sp, false)}
                  >
                    {pct >= 9 && <span style={{ fontSize: "0.68rem", fontWeight: 700, color: "#fff", whiteSpace: "nowrap", userSelect: "none" }}>{pct.toFixed(0)}%</span>}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function StackedBar({ resposta, texto, onDrillDown }) {
  if (!resposta || resposta.tipo !== "split") {
    return (
      <div className="d-flex justify-content-center align-items-center h-100 text-muted" style={{ fontSize: "0.78rem", textAlign: "center", padding: 12 }}>
        Configure os campos<br /><strong>Agrupar por</strong> e <strong>Dividir por</strong>
      </div>
    );
  }

  const { grupos, splits, matrix } = resposta;
  return (
    <div style={{ padding: "8px 12px", height: "100%", display: "flex", flexDirection: "column", gap: 0, boxSizing: "border-box" }}>
      <StackedBarRows grupos={grupos} splits={splits} matrix={matrix} texto={texto} onDrillDown={onDrillDown} />
      <div style={{ display: "flex", flexWrap: "wrap", gap: "3px 10px", paddingTop: 6, justifyContent: "flex-end", flexShrink: 0 }}>
        {splits.map((sp, si) => (
          <div key={sp} style={{ display: "flex", alignItems: "center", gap: 3, fontSize: "0.69rem", color: texto }}>
            <div style={{ width: 9, height: 9, borderRadius: 2, background: corParaLabel(sp, si), flexShrink: 0 }} />
            {sp}
          </div>
        ))}
      </div>
    </div>
  );
}


function gerarMesesIntervalo(inicio, fim) {
  const meses = [];
  let [y, m] = inicio.split("-").map(Number);
  const [fy, fm] = fim.split("-").map(Number);
  while (y < fy || (y === fy && m <= fm)) {
    meses.push(`${y}-${String(m).padStart(2, "0")}`);
    m++;
    if (m > 12) { m = 1; y++; }
  }
  return meses;
}

function extrairChaveMesAno(val) {
  const s = String(val ?? "");
  const m1 = s.match(/^(\d{2})\/(\d{2})\/(\d{4})$/); // dd/MM/yyyy
  if (m1) return `${m1[3]}-${m1[2]}`;
  if (/^\d{4}-\d{2}$/.test(s)) return s;              // YYYY-MM
  return null;
}

function TimelineBar({ dados, campoGrupo, periodoInicio, periodoFim, texto, darkMode, onDrillDown }) {
  const contagem = {};
  (dados ?? []).forEach((row) => {
    const val = row[campoGrupo];
    if (val == null || val === "") return;
    const chave = extrairChaveMesAno(val);
    if (chave) contagem[chave] = (contagem[chave] || 0) + 1;
  });

  // Determina intervalo: usa período configurado ou min/max dos dados
  const chaves = Object.keys(contagem).sort();
  const inicio = periodoInicio || chaves[0];
  const fim    = periodoFim    || chaves[chaves.length - 1];

  const todasChaves = (inicio && fim && /^\d{4}-\d{2}$/.test(inicio) && /^\d{4}-\d{2}$/.test(fim))
    ? gerarMesesIntervalo(inicio, fim)
    : chaves;

  const pontos = todasChaves.map((key) => {
    const match = key.match(/^(\d{4})-(\d{2})$/);
    return {
      key,
      label: match ? `${match[2]}/${match[1]}` : key,   // MM/AAAA
      total: contagem[key] ?? 0,
    };
  });

  if (!pontos.length) {
    return (
      <div className="d-flex justify-content-center align-items-center h-100 text-muted" style={{ fontSize: "0.78rem" }}>
        Sem dados
      </div>
    );
  }

  const maxTotal = Math.max(...pontos.map((p) => p.total), 1);
  const borda    = darkMode ? "#30364a" : "#dee2e6";

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column", padding: "10px 12px 8px", boxSizing: "border-box" }}>
      {/* Área das barras */}
      <div style={{ flex: 1, display: "flex", alignItems: "flex-end", gap: 4, minHeight: 0, overflow: "hidden" }}>
        {pontos.map((p) => {
          const hPct = (p.total / maxTotal) * 100;
          return (
            <div key={p.key} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", height: "100%", justifyContent: "flex-end" }}>
              <span style={{ fontSize: "0.62rem", fontWeight: 700, color: texto, marginBottom: 2, lineHeight: 1 }}>{p.total}</span>
              <div
                style={{ width: "65%", minWidth: 6, height: `${hPct}%`, minHeight: 3, background: "#6ea8fe", borderRadius: "3px 3px 0 0", transition: "height 0.2s", cursor: onDrillDown ? "pointer" : "default" }}
                title={`${p.label}: ${p.total}`}
                onClick={() => onDrillDown?.(p.key, null, true)}
              />
            </div>
          );
        })}
      </div>

      {/* Labels MM/AAAA */}
      <div style={{ display: "flex", gap: 4, paddingTop: 4, borderTop: `1px solid ${borda}`, marginTop: 3 }}>
        {pontos.map((p) => (
          <div key={p.key} style={{ flex: 1, textAlign: "center", fontSize: "0.58rem", color: "#6c757d", lineHeight: 1.2 }}>
            {p.label}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function DashboardWidget({ widget, dados, editMode, onEdit, onDelete, onDrillDown }) {
  const { darkMode } = useContext(GlobalContext);

  const resposta = useMemo(
    () => agregarDados(dados, widget.campo_grupo, widget.campo_split),
    [dados, widget.campo_grupo, widget.campo_split]
  );

  const bg    = darkMode ? "#1c2235" : "#fff";
  const borda = darkMode ? "#30364a" : "#e9ecef";
  const texto = darkMode ? "#e2e8f0" : "#212529";

  const chartData = buildChartData(resposta);
  const usePct = widget.tipo === "bar_horizontal" && widget.campo_split;

  // Cria as opções do Chart.js incluindo o handler de click para drill-down
  function buildOpcoes(base) {
    if (!onDrillDown || !chartData) return base;
    return {
      ...base,
      onClick: (_, activeElements) => {
        if (!activeElements.length) return;
        const { index, datasetIndex } = activeElements[0];
        const label = chartData.labels[index];
        const splitLabel = chartData.datasets.length > 1
          ? (chartData.datasets[datasetIndex]?.label ?? null)
          : null;
        onDrillDown(label, splitLabel, false);
      },
    };
  }

  function renderConteudo() {
    if (widget.tipo === "kpi") {
      const total = resposta?.total ?? (dados?.length ?? 0);
      return (
        <div
          className="d-flex flex-column justify-content-center align-items-center h-100"
          style={{ cursor: onDrillDown ? "pointer" : "default" }}
          onClick={() => onDrillDown?.(null, null, false)}
          title={onDrillDown ? "Clique para ver os documentos" : undefined}
        >
          <div style={{ fontSize: "3rem", fontWeight: 700, color: "#0d6efd", lineHeight: 1 }}>{total}</div>
        </div>
      );
    }

    if (widget.tipo === "bar_stacked")  return <StackedBar resposta={resposta} texto={texto} darkMode={darkMode} onDrillDown={onDrillDown} />;

    if (widget.tipo === "bar_timeline") return <TimelineBar dados={dados} campoGrupo={widget.campo_grupo} periodoInicio={widget.periodo_inicio} periodoFim={widget.periodo_fim} texto={texto} darkMode={darkMode} onDrillDown={onDrillDown} />;

    if (!chartData) {
      return (
        <div className="d-flex justify-content-center align-items-center h-100 text-muted" style={{ fontSize: "0.8rem" }}>
          Sem dados
        </div>
      );
    }

    const estiloChart = onDrillDown ? { cursor: "pointer" } : {};

    if (widget.tipo === "donut") return <div style={estiloChart}><Doughnut data={chartData} options={buildOpcoes(OPCOES_DONUT)} /></div>;

    if (widget.tipo === "bar_vertical") return <div style={estiloChart}><Bar data={chartData} options={buildOpcoes(OPCOES_BAR_V)} /></div>;

    if (widget.tipo === "bar_horizontal") {
      const dadosPct = usePct
        ? {
            ...chartData,
            datasets: chartData.datasets.map((ds) => ({
              ...ds,
              data: ds.data.map((v, i) => {
                const totalGrupo = chartData.datasets.reduce((s, d2) => s + (d2.data[i] ?? 0), 0);
                return totalGrupo > 0 ? +((v / totalGrupo) * 100).toFixed(1) : 0;
              }),
            })),
          }
        : chartData;
      return <div style={estiloChart}><Bar data={dadosPct} options={buildOpcoes(usePct ? OPCOES_BAR_H_PCT : OPCOES_BAR_H)} /></div>;
    }

    return null;
  }

  return (
    <div
      style={{
        background: bg,
        border: `1px solid ${borda}`,
        borderRadius: 10,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "8px 12px",
          borderBottom: `1px solid ${borda}`,
          flexShrink: 0,
        }}
      >
        <span style={{ fontSize: "0.82rem", fontWeight: 600, color: texto, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {widget.titulo || "Sem título"}
        </span>
        {editMode && (
          <div style={{ display: "flex", gap: 4, flexShrink: 0, marginLeft: 8 }}>
            <button
              onClick={onEdit}
              style={{ background: "none", border: "none", cursor: "pointer", color: "#0d6efd", padding: "2px 4px" }}
              title="Editar widget"
            >
              <FaEdit size={12} />
            </button>
            <button
              onClick={onDelete}
              style={{ background: "none", border: "none", cursor: "pointer", color: "#dc3545", padding: "2px 4px" }}
              title="Remover widget"
            >
              <FaTrash size={12} />
            </button>
          </div>
        )}
      </div>

      <div style={{ flex: 1, padding: (widget.tipo === "kpi" || widget.tipo === "bar_stacked" || widget.tipo === "bar_timeline") ? 0 : "10px 14px", minHeight: 0 }}>
        {renderConteudo()}
      </div>
    </div>
  );
}
